import UserAgentCheck from '../3rdparty/util/UserAgentCheck';
import ConsentService from './ConsentService';

/**
 * iOS ATT (App Tracking Transparency) permission status constants
 */
export const ConsentStatus = {
    ALLOWED: 'ALLOWED',
    DECLINED: 'DECLINED'
};

const NEEDED_APP_API_VERSION = '1.38';

/**
 * Service class for handling iOS ATT (App Tracking Transparency) permission checks.
 *
 * This service handles all iOS app-related permission logic including:
 * - iOS app detection
 * - ATT permission status checking (via cookie or app version)
 * - Consent cookie management for iOS apps
 */
class IOsAttService {
    /**
     * Checks if the current user agent is an iOS app.
     *
     * @param {string} [userAgent] - Optional user agent string, defaults to window.navigator.userAgent
     * @returns {boolean} True if the user agent is an iOS app
     */
    static isIOsApp(userAgent = window.navigator.userAgent) {
        return UserAgentCheck.isIOsApp(userAgent);
    }

    /**
     * Determines iOS ATT permission status based on cookie or app version.
     * Replicates the logic of the former server-side ConsentController.getPermissionForApp(), which is gone
     *
     * Priority:
     * 1. Check gdpr_att cookie - if exists, use its value ('1' = allowed, others = declined)
     * 2. If no cookie, check app version - versions >= 1.38 support cookie banner (allowed)
     *
     * @param {string} [userAgent] - Optional user agent string, defaults to window.navigator.userAgent
     * @returns {string} ConsentStatus.ALLOWED or ConsentStatus.DECLINED
     */
    static getAppPermissionStatus(userAgent = window.navigator.userAgent) {
        // Check if gdpr_att cookie exists
        const gdprAttValue = ConsentService.getGdprAttCookie();

        if (gdprAttValue !== undefined) {
            // Cookie exists: check if value is '1' (allowed)
            return gdprAttValue === '1' ? ConsentStatus.ALLOWED : ConsentStatus.DECLINED;
        }

        // No cookie: fall back to app version check
        const isSupportedAppVersion = UserAgentCheck.appVersionSupportsCookieBanner(
            userAgent,
            NEEDED_APP_API_VERSION
        );

        return isSupportedAppVersion ? ConsentStatus.ALLOWED : ConsentStatus.DECLINED;
    }

    /**
     * Checks if iOS ATT permission is allowed.
     *
     * @param {string} [userAgent] - Optional user agent string, defaults to window.navigator.userAgent
     * @returns {boolean} True if ATT permission is allowed
     */
    static isAttPermissionAllowed(userAgent = window.navigator.userAgent) {
        return this.getAppPermissionStatus(userAgent) === ConsentStatus.ALLOWED;
    }

    /**
     * Handles ATT permission check and resets consent cookies if permission is denied.
     *
     * For iOS apps:
     * - Checks ATT permission status
     * - If denied, resets all consent cookies
     * - Returns the permission status
     *
     * For non-iOS:
     * - Always returns true (allowed)
     *
     * @param {string} [userAgent] - Optional user agent string, defaults to window.navigator.userAgent
     * @returns {boolean} ATT opt-in status
     */
    static checkAttPermissionAndResetIfNeeded(userAgent = window.navigator.userAgent) {
        if (this.isIOsApp(userAgent)) {
            const attPermissionAllowed = this.isAttPermissionAllowed(userAgent);

            if (!attPermissionAllowed) {
                ConsentService.resetConsentCookies();
            }

            return attPermissionAllowed;
        }

        return true;
    }
}

export default IOsAttService;
